import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { format } from 'date-fns';

import InputForm from '../InputForm/InputForm';
import Button from '../Buttons/Button';
import api from '../../services/api';
import './SmsAccessSettings.scss';

// Mirrors the server's validateCode, so Save only enables for a code the server will accept.
const checksFor = (code) => {
    const trimmed = code.trim();
    return {
        minLength: trimmed.length >= 6,
        charset: trimmed.length > 0 && /^[A-Za-z0-9]+$/.test(trimmed),
        number: /[0-9]/.test(trimmed),
    };
};

function SmsAccessSettings() {
    const [status, setStatus] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [codes, setCodes] = useState({ newCode: '', confirmCode: '' });
    const [saving, setSaving] = useState(false);
    const [locking, setLocking] = useState(false);

    const loadStatus = useCallback(async () => {
        try {
            const { data } = await api.get('/admin/sms-access');
            setStatus(data?.data || null);
            setLoadError(false);
        } catch (err) {
            console.error('Failed to load access code status:', err);
            setLoadError(true);
        }
    }, []);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    const checks = useMemo(() => checksFor(codes.newCode), [codes.newCode]);
    const allPass = checks.minLength && checks.charset && checks.number;
    const matches = codes.newCode.trim() === codes.confirmCode.trim();

    const handleChange = (event) => {
        const { id, value } = event.target;
        setCodes((prev) => ({ ...prev, [id]: value }));
    };

    const handleSave = async () => {
        if (!allPass) {
            toast.error("The code doesn't meet the requirements yet.");
            return;
        }
        if (!matches) {
            toast.error("The codes don't match.");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.put('/admin/sms-access/code', { code: codes.newCode.trim() });
            setStatus(data?.data || null);
            setCodes({ newCode: '', confirmCode: '' });
            toast.success('Access code saved. Any open session was closed.');
        } catch (err) {
            toast.error(err?.response?.data?.message || "Couldn't save the access code.");
        } finally {
            setSaving(false);
        }
    };

    const handleLockAll = async () => {
        setLocking(true);
        try {
            const { data } = await api.post('/admin/sms-access/lock');
            setStatus(data?.data || null);
            const n = data?.data?.locked || 0;
            toast.success(n ? `Locked ${n} open session${n === 1 ? '' : 's'}.` : 'Nothing was unlocked.');
        } catch (err) {
            toast.error("Couldn't lock sessions.");
        } finally {
            setLocking(false);
        }
    };

    const idle = status?.idle_minutes || 60;
    const maxHours = status?.max_hours || 12;
    const codeStatus = status?.is_set
        ? `Set${status.updated_at ? `, changed ${format(new Date(status.updated_at), 'MMM d, h:mm a')}` : ''}`
        : 'Not set';

    return (
        <section className='sms-access'>
            <div className='sms-access__container'>
                <div className='sms-access__header'>
                    <h1>Text access code</h1>
                    <p>
                        {`Before your number can do anything by text, send this code to 866-902-3933. It stays unlocked while you're texting and locks after ${idle} minutes idle or ${maxHours} hours. Text LOCK to close it sooner.`}
                    </p>
                </div>

                {loadError && (
                    <p className='sms-access__notice is-error'>{"Couldn't load the access code status."}</p>
                )}

                {status && (
                    <div className='sms-access__status'>
                        <div className='sms-access__row'>
                            <span>Access code</span>
                            <strong className={status.is_set ? 'is-good' : 'is-warn'}>{codeStatus}</strong>
                        </div>
                        <div className='sms-access__row'>
                            <span>Unlocked right now</span>
                            <strong>{status.open_sessions}</strong>
                        </div>
                        <div className='sms-access__row'>
                            <span>Blocked after wrong codes</span>
                            <strong className={status.locked_out ? 'is-warn' : ''}>{status.locked_out}</strong>
                        </div>
                    </div>
                )}

                {status && !status.is_set && (
                    <p className='sms-access__notice is-warn'>
                        Texting from your number is locked until you set a code.
                    </p>
                )}

                <InputForm
                    id="newCode"
                    onChange={handleChange}
                    value={codes.newCode}
                    label={status?.is_set ? 'New code' : 'Code'}
                    type="password"
                />
                <ul className='sms-access__hints'>
                    <li className={checks.minLength ? 'is-valid' : ''}>At least 6 characters</li>
                    <li className={checks.charset ? 'is-valid' : ''}>Letters and numbers only</li>
                    <li className={checks.number ? 'is-valid' : ''}>At least 1 number</li>
                </ul>

                <InputForm
                    id="confirmCode"
                    onChange={handleChange}
                    value={codes.confirmCode}
                    label="Confirm code"
                    type="password"
                />

                <div className='sms-access__actions'>
                    <Button
                        primaryBtn
                        onClick={handleSave}
                        disabled={saving || !allPass || !matches}
                        label={saving ? 'Saving…' : status?.is_set ? 'Change code' : 'Set code'}
                    />
                    <button
                        type='button'
                        className='sms-access__secondary'
                        onClick={handleLockAll}
                        disabled={locking || !status?.open_sessions}
                    >
                        {locking ? 'Locking…' : 'Lock now'}
                    </button>
                </div>
            </div>
        </section>
    );
}

export default SmsAccessSettings;
